export * from '@apollo/client/core';

export type { ApolloContextValue } from '@apollo/client/react';
export { getApolloContext } from '@apollo/client/react';
export { ApolloProvider } from '@apollo/client/react';
export { useApolloClient } from '@apollo/client/react';
export { useLazyQuery } from '@apollo/client/react';
export { useQuery } from '@apollo/client/react';
export { useSubscription } from '@apollo/client/react';
export { useReactiveVar } from '@apollo/client/react';
export { useFragment } from '@apollo/client/react';
export { useSuspenseQuery } from '@apollo/client/react';
export { useBackgroundQuery } from '@apollo/client/react';
export { useSuspenseFragment } from '@apollo/client/react';
export { useLoadableQuery } from '@apollo/client/react';
export { useQueryRefHandlers } from '@apollo/client/react';
export { useReadQuery } from '@apollo/client/react';
export { skipToken } from '@apollo/client/react';
export { createQueryPreloader } from '@apollo/client/react';
export { reactCompilerVersion } from '@apollo/client/react';

export type { SkipToken } from '@apollo/client/react';
export type {
  PreloadQueryFetchPolicy,
  PreloadQueryFunction,
  PreloadQueryOptions,
} from '@apollo/client/react';
export type { PreloadedQueryRef, QueryRef } from '@apollo/client/react';
export type {
  BackgroundQueryHookFetchPolicy,
  BackgroundQueryHookOptions,
  LazyQueryExecFunction,
  LazyQueryHookExecOptions,
  LazyQueryHookOptions,
  LazyQueryResult,
  LazyQueryResultTuple,
  LoadableQueryFetchPolicy,
  LoadableQueryHookOptions,
  LoadQueryFunction,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationResult,
  MutationTuple,
  OnDataOptions,
  OnSubscriptionDataOptions,
  QueryHookOptions,
  QueryResult,
  SubscriptionHookOptions,
  SubscriptionResult,
  SuspenseQueryHookFetchPolicy,
  SuspenseQueryHookOptions,
  UseBackgroundQueryResult,
  UseFragmentOptions,
  UseFragmentResult,
  UseLoadableQueryResult,
  UseQueryRefHandlersResult,
  UseReadQueryResult,
  UseSuspenseFragmentOptions,
  UseSuspenseFragmentResult,
  UseSuspenseQueryResult,
} from '@apollo/client/react';

import * as React from 'react';
import type { FetchResult, OperationVariables } from '@apollo/client/core';
import type { MutationFunctionOptions, MutationHookOptions } from '@apollo/client/react';
import { useMutation as useMutationBase } from '@apollo/client/react';

export type MutationFunction<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = (options?: MutationFunctionOptions<TData, TVariables>) => Promise<FetchResult<TData>>;

export type ApolloError = Error & {
  graphQLErrors: ReadonlyArray<unknown>;
  networkError?: unknown;
};

export type BaseQueryOptions = {
  variables?: Record<string, unknown>;
};

export type BaseMutationOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = MutationHookOptions<TData, TVariables>;

function toLegacyErrors(error: unknown): ReadonlyArray<unknown> | undefined {
  if (!error) return undefined;
  const anyError = error as { errors?: unknown; message?: unknown };
  if (Array.isArray(anyError.errors)) return anyError.errors;
  return [{ message: String(anyError.message ?? error) }];
}

// Apollo Client v3 codegen expects `result.errors` (array) from `mutate(...)`.
// Apollo Client v4 returns `{ error?: ErrorLike }` instead, so we normalize.
export function useMutation<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(...args: any[]): any {
  const [mutate, result] = (useMutationBase as any)(...args);

  const wrappedMutate = React.useCallback(async (...mutateArgs: any[]) => {
    const res = await mutate(...mutateArgs);
    const errors = (res as any)?.errors ?? toLegacyErrors((res as any)?.error);
    return errors ? { ...(res as any), errors } : res;
  }, [mutate]);

  const errors = (result as any)?.errors ?? toLegacyErrors((result as any)?.error);
  const wrappedResult = errors ? { ...(result as any), errors } : result;

  return [wrappedMutate, wrappedResult] as any;
}
